import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { ControlBarButton } from '@m12s/component-library';
import { useMutation } from '@apollo/client';
import { check, times } from 'lib/icons';
import { FAISubmitWorkPromptModal } from 'components/FirstArticleInspection/FAISubmitWorkPromptModal';
import { DISMISS_FAI, REQUEST_FAI } from 'lib/api/mutations';
import useStartSubmitWork from 'lib/hooks/useStartSubmitWork';
import { FAI_STATUS } from 'lib/constants';
import { getCanRequestFAI } from 'lib/selectors/getCanRequestFAI';
import { getCurrentOperator } from 'lib/selectors/getCurrentOperator';
import { getFAICurrentInspection } from 'lib/selectors/getFAICurrentInspection';
import { getFAILoading } from 'lib/selectors/getFAILoading';
import { getLatestActivitySet } from 'lib/selectors/getLatestActivitySet';
import { getMachine } from 'lib/selectors/getMachine';
import { MobileIcon, ResponsiveText } from '../styled';

/**
 * Renders the ERP submit-work control-bar action with first-article inspection
 * gating.
 *
 * When an inspection is pending, the control switches to a cancel-inspection
 * action. When in progress, the same label is shown disabled (no dismiss until
 * the inspector finishes). Otherwise, submit work either opens the FAI prompt
 * for eligible jobs or starts the submit-work flow immediately.
 */
const SubmitWorkOrFAISection = () => {
  const { t } = useTranslation();
  const machine = useSelector(getMachine);
  const canRequestFAI = useSelector(getCanRequestFAI);
  const operator = useSelector(getCurrentOperator);
  const latestActivitySet = useSelector(getLatestActivitySet);
  const currentInspection = useSelector(getFAICurrentInspection);
  const isInspectionPending = currentInspection?.status === FAI_STATUS.PENDING;
  const isInspectionInProgress =
    currentInspection?.status === FAI_STATUS.IN_PROGRESS;
  const workOrderOperationRef = latestActivitySet?.workOrderOperationRef;
  const operatorId = operator?.id;
  const machineRef = machine?.machineRef;
  const [requestFAI, { loading: requestFAILoading }] = useMutation(REQUEST_FAI);
  const [dismissFAI] = useMutation(DISMISS_FAI);
  const [isSubmitPromptOpen, setIsSubmitPromptOpen] = useState(false);
  const faiLoading = useSelector(getFAILoading);
  const startSubmitWork = useStartSubmitWork();

  const handleSendForInspection = useCallback(() => {
    return requestFAI({
      variables: {
        input: {
          machineRef,
          workOrderOperationRef,
          operatorId,
        },
      },
    }).then(() => {
      setIsSubmitPromptOpen(false);
    });
  }, [requestFAI, machineRef, workOrderOperationRef, operatorId]);

  return (
    <>
      <FAISubmitWorkPromptModal
        isOpen={isSubmitPromptOpen}
        onClose={() => {
          setIsSubmitPromptOpen(false);
        }}
        onSkip={() => {
          setIsSubmitPromptOpen(false);
          startSubmitWork();
        }}
        onSendForInspection={handleSendForInspection}
        sendLoading={requestFAILoading}
      />

      {isInspectionPending ? (
        <ControlBarButton
          id="job-control-bar-cancel-inspection"
          onClick={() => {
            return dismissFAI({
              variables: {
                input: {
                  inspectionRef: currentInspection.inspectionRef,
                },
              },
            });
          }}
        >
          <MobileIcon icon={times} />
          <ResponsiveText
            full={t('fai.cancelInspection|full')}
            medium={t('fai.cancelInspection|medium')}
            short={t('fai.cancelInspection|short')}
          />
        </ControlBarButton>
      ) : isInspectionInProgress ? (
        <ControlBarButton
          id="job-control-bar-cancel-inspection-disabled"
          disabled
          aria-disabled
          style={{ cursor: 'not-allowed' }}
        >
          <MobileIcon icon={times} />
          <ResponsiveText
            full={t('fai.cancelInspection|full')}
            medium={t('fai.cancelInspection|medium')}
            short={t('fai.cancelInspection|short')}
          />
        </ControlBarButton>
      ) : (
        <ControlBarButton
          id="job-control-bar-submit"
          disabled={faiLoading}
          aria-disabled={faiLoading}
          aria-busy={faiLoading}
          style={{
            cursor: faiLoading ? 'not-allowed' : undefined,
          }}
          onClick={() => {
            if (faiLoading) return null;

            if (canRequestFAI) {
              return setIsSubmitPromptOpen(true);
            }
            return startSubmitWork();
          }}
        >
          <MobileIcon icon={check} />
          <ResponsiveText
            full={t('submitWork|full')}
            medium={t('submitWork|medium')}
            short={t('submitWork|short')}
          />
        </ControlBarButton>
      )}
    </>
  );
};

export { SubmitWorkOrFAISection };
